var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useMemo } from 'react';
import clsx from 'clsx';
import { useClasses, useLayoutProps, useProps, useTranslate } from '../hooks';
import { getComputedStyleFromLayoutProps } from '../util';
import { SkeletonLoaderStrings } from './skeletonLoader.strings';
import { useSkeletonLoaderStyles } from './skeletonLoader.styles';
var displayName = 'SkeletonLoader';
/**
 * `<SkeletonLoader>` functions as a placeholder for parts of your application that are loading.
 */
export var SkeletonLoader = forwardRef(function (_a, ref) {
    var _b;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _c = useProps(displayName, props), classesProp = _c.classes, className = _c.className, _d = _c.component, Component = _d === void 0 ? 'div' : _d, height = _c.height, layout = _c.layout, marginBottom = _c.marginBottom, marginLeft = _c.marginLeft, marginRight = _c.marginRight, marginTop = _c.marginTop, maxHeight = _c.maxHeight, maxWidth = _c.maxWidth, minHeight = _c.minHeight, minWidth = _c.minWidth, styleProp = _c.style, translateProp = _c.translate, _e = _c.variant, variant = _e === void 0 ? 'square' : _e, width = _c.width, restProps = __rest(_c, ["classes", "className", "component", "height", "layout", "marginBottom", "marginLeft", "marginRight", "marginTop", "maxHeight", "maxWidth", "minHeight", "minWidth", "style", "translate", "variant", "width"]);
    // Strings
    var translate = useTranslate(translateProp, SkeletonLoaderStrings);
    // Styles
    var baseClasses = useSkeletonLoaderStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var baseStyle = useMemo(function () {
        var layoutProps = {};
        if (height !== undefined)
            layoutProps.height = height;
        if (marginBottom !== undefined)
            layoutProps.marginBottom = marginBottom;
        if (marginLeft !== undefined)
            layoutProps.marginLeft = marginLeft;
        if (marginRight !== undefined)
            layoutProps.marginRight = marginRight;
        if (marginTop !== undefined)
            layoutProps.marginTop = marginTop;
        if (maxHeight !== undefined)
            layoutProps.maxHeight = maxHeight;
        if (maxWidth !== undefined)
            layoutProps.maxWidth = maxWidth;
        if (minHeight !== undefined)
            layoutProps.minHeight = minHeight;
        if (minWidth !== undefined)
            layoutProps.minWidth = minWidth;
        if (width !== undefined)
            layoutProps.width = width;
        return getComputedStyleFromLayoutProps(styleProp, layoutProps);
    }, [
        height,
        marginBottom,
        marginLeft,
        marginRight,
        marginTop,
        maxHeight,
        maxWidth,
        minHeight,
        minWidth,
        styleProp,
        width,
    ]);
    var style = useLayoutProps({ layout: layout, style: baseStyle });
    var rootClassName = clsx(classes.HAWKINS_BASE, classes.root, className, (_b = {},
        _b[classes.rootCircle] = variant === 'circle',
        _b[classes.rootSquare] = variant === 'square',
        _b[classes.rootText] = variant === 'text',
        _b));
    return (_jsx(Component, __assign({ "aria-label": translate('skeletonLoader.ariaLabel'), className: rootClassName, ref: ref, role: "progressbar", style: style }, restProps, { children: children })));
});
SkeletonLoader.displayName = displayName;
